import React, { useEffect, useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, Stack, TextField, Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import financeService from '../../../services/financeService';
import { FEE_REQUEST_META, FeeRequestKind, FeeRequestLimits } from '../../../types/finance';
import { PAYMENT_METHOD_OPTIONS, PaymentMethod } from '../../../utils/paymentMethods';
import { useAuth } from '../../../context/AuthContext';
import { useCurrencyFormat } from '../../../hooks/useCurrencyFormat';
import SearchableSelect from '../../common/SearchableSelect';

/**
 * Raise a write-off, refund or deposit return on a challan. Shows how much
 * is available; the server checks again when it is approved.
 */
const FeeRequestDialog: React.FC<{
  kind: FeeRequestKind | null;
  invoice: { _id: string; invoiceNumber: string } | null;
  limits: FeeRequestLimits | null;
  onClose: () => void;
  onSaved: () => void;
}> = ({ kind, invoice, limits, onClose, onSaved }) => {
  const { currency } = useAuth();
  const money = useCurrencyFormat();
  const { enqueueSnackbar } = useSnackbar();
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<PaymentMethod | ''>('cash');
  const [reference, setReference] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const meta = kind ? FEE_REQUEST_META[kind] : null;
  const max = !kind || !limits ? 0 : kind === 'WRITE_OFF' ? limits.writeOff.max : kind === 'REFUND' ? limits.refund : limits.deposit;
  const blocked = kind === 'WRITE_OFF' && !!limits && !limits.writeOff.eligible;

  useEffect(() => {
    if (!kind) return;
    // A write-off usually takes the whole balance; money going back is entered deliberately.
    setAmount(kind === 'WRITE_OFF' && max ? String(max) : '');
    setMethod('cash');
    setReference('');
    setReason('');
    setError(null);
  }, [kind, max]);

  const value = Number(amount);
  const valid = !blocked && value > 0 && value <= max + 0.005 && !!reason.trim() && (!meta?.paysOut || !!method);

  const submit = async () => {
    if (!kind || !invoice) return;
    setBusy(true);
    setError(null);
    try {
      const r = await financeService.createFeeRequest({
        kind, invoiceId: invoice._id, amount: value, reason: reason.trim(),
        ...(meta?.paysOut ? { method, reference: reference.trim() || undefined } : {}),
      });
      enqueueSnackbar(r.status === 'POSTED' ? `${meta!.label} recorded` : `${meta!.label} sent for approval`, { variant: 'success' });
      onSaved();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Could not save this.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!kind} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        {meta?.verb}
        <Typography variant="body2" color="text.secondary">Challan {invoice?.invoiceNumber} · {meta?.hint}</Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: '16px !important' }}>
        <Stack spacing={2}>
          {blocked && <Alert severity="warning" variant="outlined">Not eligible yet — {limits!.writeOff.reason.toLowerCase()}.</Alert>}
          <TextField
            label="Amount"
            size="small"
            value={amount}
            onChange={e => /^\d*(\.\d{0,2})?$/.test(e.target.value) && setAmount(e.target.value)}
            disabled={blocked || busy}
            helperText={`Up to ${money(max)}`}
            error={value > max + 0.005}
            InputProps={{ startAdornment: <InputAdornment position="start">{currency}</InputAdornment> }}
            inputProps={{ inputMode: 'decimal' }}
          />
          {meta?.paysOut && (
            <Stack direction="row" spacing={1.5}>
              <SearchableSelect label="Paid back by" value={method} onChange={v => setMethod(v as PaymentMethod)} options={PAYMENT_METHOD_OPTIONS} clearable={false} fullWidth minWidth={0} disabled={busy} />
              <TextField label="Reference" size="small" value={reference} onChange={e => setReference(e.target.value)} placeholder="Optional" disabled={busy} fullWidth />
            </Stack>
          )}
          <TextField
            label="Reason"
            size="small"
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Kept with the record for audit"
            multiline
            minRows={2}
            inputProps={{ maxLength: 500 }}
            disabled={blocked || busy}
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={busy}>Cancel</Button>
        <Button variant="contained" disableElevation onClick={submit} disabled={!valid || busy}>
          {busy ? 'Saving…' : limits?.requiresApproval ? 'Send for approval' : meta?.verb}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FeeRequestDialog;
